'use client'

import { useTranslations } from 'next-intl'
import { PropsWithChildren } from 'react'

import { TRoomDetail, TSimpleReviewCount } from '@/entities/room'
import { cn } from '@/shared/lib'
import { Button, Drawer, Separator } from '@/shared/ui'

import { ReviewList } from './review-list'
import { ReviewScore } from './review-score'
import { SimpleReview } from './simple-review'

type Props = Pick<TRoomDetail, 'reviewList' | 'reviewScore'> & {
  roomSimpleReview: TSimpleReviewCount[]
}

export const ReviewDrawerTrigger = ({
  children,
  reviewList,
  reviewScore,
  roomSimpleReview,
}: PropsWithChildren<Props>) => {
  return (
    <Drawer.Root>
      <Drawer.Trigger asChild>{children}</Drawer.Trigger>
      <ReviewDrawer
        reviewList={reviewList}
        reviewScore={reviewScore}
        roomSimpleReview={roomSimpleReview}
      />
    </Drawer.Root>
  )
}

export const ReviewDrawer = ({
  reviewList,
  reviewScore,
  roomSimpleReview,
}: Props) => {
  const t = useTranslations('room_detail')

  const hasSimpleReview = roomSimpleReview.length > 0

  return (
    <Drawer.Content className="flex max-h-[95dvh] flex-col gap-0 p-0">
      <Drawer.Header className="px-4 pb-5">
        <Drawer.Title>{t('review.title')}</Drawer.Title>
        <Drawer.Description className="sr-only">
          {t('review.title')}
        </Drawer.Description>
      </Drawer.Header>
      <div className="flex grow flex-col overflow-y-auto px-4">
        <ReviewScore
          reviewScore={reviewScore}
          className={cn('mb-6', hasSimpleReview && 'mb-4')}
        />
        {hasSimpleReview && (
          <>
            <SimpleReview simpleReviews={roomSimpleReview} />
            <Separator color="gray" className="my-6" />
          </>
        )}
        <ReviewList
          reviewList={reviewList}
          showAll
          showCount
          className="space-y-3"
        />
      </div>
      <Drawer.Footer className="px-4 py-6">
        <Drawer.Close asChild>
          <Button size="large" color="secondary">
            {t('review.button.close')}
          </Button>
        </Drawer.Close>
      </Drawer.Footer>
    </Drawer.Content>
  )
}
